import { useEffect, useState, type ReactNode } from 'react'
import { LayoutGrid, Settings, ShieldCheck, LogOut, Menu, X, ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { cn } from '../../lib/cn'
import { navigate, usePath } from '../../lib/router'
import { useAuthStore } from '../../lib/auth-store'
import { api } from '../../lib/api'
import { Wordmark, Logomark } from '../brand/Wordmark'
import { Button } from '../ui/button'
import { ThemeToggle } from '../ui/theme-toggle'

/**
 * doWallet app shell: sticky sidebar (w-60) with grouped nav, sticky glass
 * topbar (h-16) with breadcrumb + user menu, main column max-w-[96rem].
 * Pages render inside with a `space-y-8` root and start with <PageHeader>.
 */
type NavItem = { href: string; label: string; icon: ReactNode; match?: RegExp }

export function AppShell({ children, crumbs }: { children: ReactNode; crumbs?: Array<{ label: string; href?: string }> }) {
  const path = usePath()
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)
  const refreshToken = useAuthStore((s) => s.refreshToken)
  const isAdmin = useAuthStore((s) => s.isPlatformAdmin)
  const clear = useAuthStore((s) => s.clear)
  const [mobileOpen, setMobileOpen] = useState(false)
  useEffect(() => { setMobileOpen(false) }, [path])

  // Collapsible sidebar (desktop): remembered per browser; `[` toggles it.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('doable-sidebar') === 'collapsed' } catch { return false }
  })
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c
      try { localStorage.setItem('doable-sidebar', next ? 'collapsed' : 'expanded') } catch { /* private mode */ }
      return next
    })
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return
      e.preventDefault()
      toggleCollapsed()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const items: NavItem[] = [
    { href: '/dashboard', label: 'Projects', icon: <LayoutGrid />, match: /^\/(dashboard|projects)/ },
    { href: '/settings', label: 'Settings', icon: <Settings />, match: /^\/settings/ },
  ]
  if (isAdmin) items.push({ href: '/admin', label: 'Admin', icon: <ShieldCheck />, match: /^\/admin/ })

  async function logout() {
    try { if (refreshToken) await api.logout(refreshToken) } catch { /* session may already be gone */ }
    clear()
    navigate('/login')
  }

  const Nav = ({ collapsed = false }: { collapsed?: boolean }) => (
    <nav className={cn('flex flex-1 flex-col gap-1 py-4', collapsed ? 'items-center px-2' : 'px-3')}>
      <p className={cn('px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70', collapsed && 'sr-only')}>Workspace</p>
      {items.map((it) => {
        const active = it.match ? it.match.test(path) : path === it.href
        return (
          <a
            key={it.href}
            href={it.href}
            onClick={(e) => { e.preventDefault(); navigate(it.href) }}
            aria-current={active ? 'page' : undefined}
            title={collapsed ? it.label : undefined}
            aria-label={collapsed ? it.label : undefined}
            className={cn(
              'flex min-w-0 items-center rounded-lg text-sm font-medium transition-colors [&_svg]:size-[18px] [&_svg]:shrink-0',
              collapsed ? 'size-10 justify-center' : 'w-full gap-3 px-3 py-2',
              active ? 'bg-volt-soft text-volt-ink [&_svg]:stroke-[2.2]' : 'text-muted-foreground hover:bg-muted hover:text-foreground [&_svg]:stroke-[1.8]',
            )}
          >
            {it.icon}
            {!collapsed && <span className="truncate">{it.label}</span>}
          </a>
        )
      })}
    </nav>
  )

  const collapseToggle = (
    <div className={cn('flex px-3 pb-2', collapsed ? 'justify-center' : 'justify-end')}>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar  [' : 'Collapse sidebar  ['}
        onClick={toggleCollapsed}
      >
        {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
      </Button>
    </div>
  )

  const userRail = (
    <div className="flex flex-col items-center gap-1 border-t px-2 py-3">
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-semibold text-background"
        title={`${user?.name ?? user?.email ?? ''}${tenant?.name ? ` · ${tenant.name}` : ''}`}
      >
        {(user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
      </span>
      <Button variant="ghost" size="icon-sm" aria-label="Log out" title="Log out" onClick={logout}><LogOut /></Button>
    </div>
  )

  const userBlock = (
    <div className="border-t px-3 py-3">
      <div className="flex items-center gap-3 rounded-lg px-2 py-1.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-semibold text-background">
          {(user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{user?.name ?? user?.email}</p>
          <p className="truncate text-xs text-muted-foreground">{tenant?.name ?? tenant?.slug}{tenant?.role ? ` · ${tenant.role}` : ''}</p>
        </div>
        <Button variant="ghost" size="icon-sm" aria-label="Log out" title="Log out" onClick={logout}><LogOut /></Button>
      </div>
    </div>
  )

  return (
    <div className="flex min-h-svh bg-background">
      {/* Sidebar (desktop) */}
      <aside className={cn('sticky top-0 z-40 hidden h-svh shrink-0 flex-col border-r bg-background transition-[width] duration-200 ease-out lg:flex', collapsed ? 'w-16' : 'w-60')}>
        <a
          href="/dashboard"
          onClick={(e) => { e.preventDefault(); navigate('/dashboard') }}
          className={cn('flex h-16 items-center transition-opacity hover:opacity-70', collapsed ? 'justify-center px-0' : 'px-5')}
          aria-label="Doable home"
        >
          {collapsed ? <Logomark className="size-7" /> : <Wordmark />}
        </a>
        <Nav collapsed={collapsed} />
        {collapseToggle}
        {collapsed ? userRail : userBlock}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-foreground/45 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="animate-dw-fade-in absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-background shadow-float">
            <div className="flex h-16 items-center justify-between px-5">
              <Wordmark />
              <Button variant="ghost" size="icon" aria-label="Close menu" onClick={() => setMobileOpen(false)}><X /></Button>
            </div>
            <Nav />
            {userBlock}
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <div className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu" onClick={() => setMobileOpen(true)}><Menu /></Button>
          <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard') }} className="lg:hidden"><Logomark className="size-7" /></a>
          <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-sm">
            <span className="hidden truncate text-muted-foreground sm:inline">{tenant?.name ?? 'Workspace'}</span>
            {(crumbs ?? []).map((c, i) => (
              <span key={i} className="flex min-w-0 items-center gap-1">
                <ChevronRight className="hidden size-3.5 shrink-0 text-muted-foreground/60 sm:inline" />
                {c.href ? (
                  <a href={c.href} onClick={(e) => { e.preventDefault(); navigate(c.href!) }} className="truncate text-muted-foreground hover:text-foreground">{c.label}</a>
                ) : (
                  <span className="truncate font-medium text-foreground">{c.label}</span>
                )}
              </span>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            {isAdmin ? <Button variant="outline" size="sm" className="hidden sm:inline-flex" onClick={() => navigate('/admin')}><ShieldCheck /> Admin</Button> : null}
            <Button variant="ghost" size="sm" onClick={logout} className="hidden sm:inline-flex"><LogOut /> Log out</Button>
          </div>
        </div>
        <main className="mx-auto w-full max-w-[96rem] flex-1 px-4 py-5 sm:px-6 sm:py-6">
          <div className="space-y-6">{children}</div>
        </main>
      </div>
    </div>
  )
}
